"use client";

import { useEffect } from "react";
import type { FormValidateOrFn, StandardSchemaV1 } from "@tanstack/form-core";
import { useForm, type ReactFormExtendedApi } from "@tanstack/react-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type BaseEntity = { id: number };

/** Form API for configurable list dialogs (TanStack Form + Zod on submit). */
export type ItemsListCardFormApi<TCreate> = ReactFormExtendedApi<
  TCreate,
  undefined,
  undefined,
  undefined,
  undefined,
  undefined,
  FormValidateOrFn<TCreate>,
  undefined,
  undefined,
  undefined,
  undefined,
  never
>;

/** Zod (Standard Schema) — input/output may differ from full form value types. */
export type ItemsListCardSchema = StandardSchemaV1<unknown, unknown>;

interface ItemsListCardDialogProps<T extends BaseEntity, TCreate> {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: T | null;
  createSchema: ItemsListCardSchema;
  editSchema: ItemsListCardSchema;
  defaultCreateValues: TCreate;
  renderFormFields: (form: ItemsListCardFormApi<TCreate>) => React.ReactNode;
  onCreate(data: TCreate): Promise<void>;
  onUpdate(data: T): Promise<void>;
}

export function ItemsListCardDialog<
  T extends BaseEntity,
  TCreate = Omit<T, "id">,
>({
  title,
  open,
  onOpenChange,
  item,
  createSchema,
  editSchema,
  defaultCreateValues,
  renderFormFields,
  onCreate,
  onUpdate,
}: ItemsListCardDialogProps<T, TCreate>) {
  const isEdit = !!item;

  const form = useForm<
    TCreate,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    FormValidateOrFn<TCreate>,
    undefined,
    undefined,
    undefined,
    undefined,
    never
  >({
    defaultValues: (item ?? defaultCreateValues) as TCreate,
    validators: {
      onSubmit: (isEdit
        ? editSchema
        : createSchema) as FormValidateOrFn<TCreate>,
    },
    onSubmit: async ({ value }) => {
      try {
        if (isEdit && item) {
          await onUpdate({ ...item, ...value });
        } else {
          await onCreate(value);
        }

        onOpenChange(false);
        form.reset();
      } catch {
        toast.error("Une erreur est survenue.");
      }
    },
  });

  useEffect(() => {
    if (open) return;
    if (item) {
      form.reset(item as unknown as TCreate);
    } else {
      form.reset(defaultCreateValues);
    }
  }, [item, defaultCreateValues, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{isEdit ? `Modifier — ${title}` : title}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-6"
        >
          {renderFormFields(form)}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                form.reset(defaultCreateValues);
              }}
            >
              Annuler
            </Button>

            <Button type="submit">{isEdit ? "Enregistrer" : "Créer"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
